import { Vector2, Vector3 } from 'three';

export function getPointInBetween(pointA, pointB, percentage) {
  let dir = pointB.clone().sub(pointA);
  const len = dir.length();
  dir = dir.normalize().multiplyScalar(len * percentage);
  return pointA.clone().add(dir);
}

export function getArea(points, signed) {
  const l = points.length;
  let det = 0;
  const isSigned = signed || false;

  if (points[0] !== points[points.length - 1])
    points = points.concat(points[0]);

  for (let i = 0; i < l; i++)
    det += points[i].x * points[i + 1].y - points[i].y * points[i + 1].x;
  if (isSigned) return det / 2;
  return Math.abs(det) / 2;
}

export function reduceEqually(p1, p2, r) {
  const r2 = r / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const mag = Math.hypot(dx, dy);
  return [
    {
      x: p1.x + (r2 * dx) / mag,
      y: p1.y + (r2 * dy) / mag,
    },
    {
      x: p2.x - (r2 * dx) / mag,
      y: p2.y - (r2 * dy) / mag,
    },
  ];
}

export function isLeft(p1, p2, p) {
  return (p2.x - p1.x) * (p.y - p1.y) - (p2.y - p1.y) * (p.x - p1.x) > 0;
}

export function distanceFromSegment(x, y, x1, y1, x2, y2) {
  const A = x - x1;
  const B = y - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const len_sq = C * C + D * D;
  let param = -1;
  if (len_sq !== 0) param = dot / len_sq;

  let xx;
  let yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  const dx = x - xx;
  const dy = y - yy;
  return Math.sqrt(dx * dx + dy * dy);
}

export function vecToScreenPosition(vec, camera) {
  const widthHalf = 0.5 * window.innerWidth;
  const heightHalf = 0.5 * window.innerHeight;
  vec.project(camera);
  vec.x = vec.x * widthHalf + widthHalf;
  vec.y = -(vec.y * heightHalf) + heightHalf;
  return new Vector2(vec.x, vec.y);
}

export function toScreenPosition(obj, camera) {
  const vector = new Vector3();

  const widthHalf = 0.5 * window.innerWidth;
  const heightHalf = 0.5 * window.innerHeight;

  obj.updateMatrixWorld();
  vector.setFromMatrixPosition(obj.matrixWorld);
  vector.project(camera);

  vector.x = vector.x * widthHalf + widthHalf;
  vector.y = -(vector.y * heightHalf) + heightHalf;

  return new Vector2(vector.x, vector.y);
}

export function lineIntersection(pointA, pointB, pointC, pointD) {
  const z1 = pointA.x - pointB.x;
  const z2 = pointC.x - pointD.x;
  const z3 = pointA.y - pointB.y;
  const z4 = pointC.y - pointD.y;
  const dist = z1 * z4 - z3 * z2;
  if (dist === 0) {
    return null;
  }
  const tempA = pointA.x * pointB.y - pointA.y * pointB.x;
  const tempB = pointC.x * pointD.y - pointC.y * pointD.x;
  const xCoor = (tempA * z2 - z1 * tempB) / dist;
  const yCoor = (tempA * z4 - z3 * tempB) / dist;

  if (
    xCoor < Math.min(pointA.x, pointB.x) ||
    xCoor > Math.max(pointA.x, pointB.x) ||
    xCoor < Math.min(pointC.x, pointD.x) ||
    xCoor > Math.max(pointC.x, pointD.x)
  ) {
    return null;
  }
  if (
    yCoor < Math.min(pointA.y, pointB.y) ||
    yCoor > Math.max(pointA.y, pointB.y) ||
    yCoor < Math.min(pointC.y, pointD.y) ||
    yCoor > Math.max(pointC.y, pointD.y)
  ) {
    return null;
  }

  return new Vector2(xCoor, yCoor);
}
export function intersect(p1, p2, p3, p4) {
  // Check if none of the lines are of length 0
  if (
    (p1.x === p2.x && p1.y.x === p2.y.y) ||
    (p3.x === p4.x && p3.y === p4.y)
  ) {
    return false;
  }

  const denominator =
    (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);

  // Lines are parallel
  if (denominator === 0) {
    return false;
  }

  const ua =
    ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) /
    denominator;
  const ub =
    ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) /
    denominator;

  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false;
  }

  // Return a object with the x and y coordinates of the intersection
  const x = p1.x + ua * (p2.x - p1.x);
  const y = p1.y + ua * (p2.y - p1.y);

  return new Vector2(x, y);
}

export function get_polygon_centroid(points) {
  const l = points.length;

  return points.reduce(
    function (center, p, i) {
      center.x += p.x;
      center.y += p.y;

      if (i === l - 1) {
        center.x /= l;
        center.y /= l;
      }

      return center;
    },
    { x: 0, y: 0 },
  );
}
